import type { Appointment, Encounter, Reference } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import * as appointments from '@seen/core/models/appointments/index'
import { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { AppointmentEncounter } from '@seen/core/models/encounters/appointment-encounter'
import { ExtendedEncounter } from '@seen/core/models/encounters/encounter'
import { NonVisitEncounter } from '@seen/core/models/encounters/nonVisitEncounter'
import type { Participant } from '@seen/core/models/patients/participant'
import { now } from '@seen/core/utils'
import {
  Button,
  DotSeparator,
  IconCalendar,
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from '@seen/ui'
import { IconLinkPlus, IconPencil } from '@tabler/icons-react'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { type JSX, memo, useContext, useState } from 'react'
import { AppointmentDetails } from '#src/components/appointment/AppointmentDetails.tsx'
import { AppointmentCombobox } from '#src/components/AppointmentCombobox.tsx'
import {
  Avatar,
  DropdownMenuItem,
  Loader,
  Sheet,
  SheetContent,
} from '#src/components/core/index.ts'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { useLinkAppointment } from '#src/components/rich-text-editor/EncounterNode/useLinkAppointment.ts'
import { useTransferEncounterData } from '#src/components/rich-text-editor/EncounterNode/useTransferEncounterData.ts'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { CurrentParticipantContext } from '#src/contexts/index.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { readOptions } from '#src/query-options/readOptions.ts'

export function EncounterNodeView({ editor }: NodeViewProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const isEditable = editor.isEditable && !isReadOnly
  return <EncounterNodeViewInner isEditable={isEditable} />
}

const EncounterNodeViewInner = memo(function EncounterNodeViewInner({
  isEditable,
}: {
  isEditable: boolean
}): JSX.Element {
  return (
    <NodeViewWrapper as="div">
      <Item variant="outline">
        <ItemMedia>
          <IconCalendar />
        </ItemMedia>
        <EncounterNodeViewItemInner isEditable={isEditable} />
      </Item>
    </NodeViewWrapper>
  )
})

function EncounterNodeViewItemInner({
  isEditable,
}: {
  isEditable: boolean
}): JSX.Element {
  const compositionReferenceString = useContext(CompositionContext)
  const participant = useContext(CurrentParticipantContext)
  const currentPractitioner = useCurrentPractitioner()

  const { data: composition, isLoading: isLoadingComposition } = useRead(
    ExtendedComposition,
    compositionReferenceString
  )

  const { data: appointment, isLoading: isLoadingAppointment } = useSearchOne(
    appointments.ExtendedAppointment,
    composition?.encounter
      ? {
          '_has:Encounter:appointment:_id': composition.encounter,
        }
      : undefined
  )

  const transaction = useTransactionMutation()
  const transferEncounterData = useTransferEncounterData()

  if (appointment) {
    return (
      <AppointmentItemInner
        appointment={appointment}
        encounterRef={composition?.encounter}
        isEditable={isEditable}
        onSelectEdit={async () => {
          invariant(composition, 'Composition is required')
          invariant(participant, 'Participant is required')

          await transaction.mutateAsync(
            async (txn, { queryClient, medplum }) => {
              const newEncounter = new NonVisitEncounter({
                patient: participant.reference,
                practitioner: currentPractitioner.reference,
                startTime: now(),
              })

              if (composition.encounter) {
                const oldEncounter = await queryClient.fetchQuery(
                  readOptions(medplum, ExtendedEncounter, composition.encounter)
                )
                if (oldEncounter) {
                  newEncounter.diagnosis = oldEncounter.diagnosis
                }
              }

              const newEncounterRef = txn.create(newEncounter)

              if (composition.encounter) {
                await transferEncounterData({
                  txn,
                  oldEncounterRef: composition.encounter,
                  newEncounterRef,
                  patientRef: participant.reference,
                })
              }

              txn.patch(composition, (c) => {
                c.encounter = newEncounterRef
              })
            }
          )
        }}
      />
    )
  }

  if (isLoadingComposition || isLoadingAppointment) {
    return (
      <ItemContent className="h-10">
        <Loader />
      </ItemContent>
    )
  }

  if (!isEditable) {
    return (
      <ItemContent>
        <ItemTitle className="h-10">
          Unlock to link this note to its appointment
        </ItemTitle>
      </ItemContent>
    )
  }

  return (
    <AppointmentSelectItemInner
      composition={composition}
      participant={participant}
    />
  )
}

function AppointmentItemInner({
  appointment,
  encounterRef,
  onSelectEdit,
  isEditable,
}: {
  appointment: appointments.ExtendedAppointment
  encounterRef: Reference<Encounter> | undefined
  onSelectEdit: () => void
  isEditable: boolean
}): JSX.Element {
  const [selectedAppointment, setSelectedAppointment] =
    useState<Reference<Appointment> | null>(null)

  return (
    <>
      <ItemContent>
        <ItemTitle className="min-h-10">
          <h2 className="flex items-center gap-1">
            <Button
              variant="link"
              onClick={() => {
                setSelectedAppointment(appointment.reference)
              }}
            >
              {appointment.getDisplay()}
            </Button>
            <DotSeparator />
            <span className="text-foreground-secondary">
              <DateTimeDisplay value={appointment.start} />
            </span>
          </h2>
          {appointment.practitioners.length > 0 && (
            <Avatar profile={appointment.practitioners[0]} size="icon" />
          )}
          <Sheet
            open={!!selectedAppointment}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedAppointment(null)
              }
            }}
          >
            <SheetContent side="right" className="max-w-xl">
              {selectedAppointment && (
                <AppointmentDetails appointmentRef={selectedAppointment} />
              )}
            </SheetContent>
          </Sheet>
        </ItemTitle>
      </ItemContent>
      {isEditable && (
        <ItemActions>
          <MoreDropdownMenu>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onSelect={onSelectEdit}
            >
              <IconPencil className="-mt-px size-4 text-foreground-secondary" />
              Edit
            </DropdownMenuItem>
            {encounterRef && <DropdownMenuMedplumLinkItem to={encounterRef} />}
          </MoreDropdownMenu>
        </ItemActions>
      )}
    </>
  )
}

function AppointmentSelectItemInner({
  composition,
  participant,
}: {
  composition: ExtendedComposition | undefined
  participant: Participant | null
}): JSX.Element {
  const { data: allAppointments, isLoading: isLoadingAllAppointments } =
    useSearch(appointments.ExtendedAppointment, {
      _sort: '-_lastUpdated',
      _count: '1000',
      patient: participant?.reference,
    })

  const { linkAppointment, isPending } = useLinkAppointment({
    composition,
    participant,
  })

  const currentTime = new Date()
  const closestAppointments = allAppointments
    ?.filter((appt) => appt.start)
    .sort((a, b) => {
      const aTime = a.start
        ? Math.abs(new Date(a.start).getTime() - currentTime.getTime())
        : Infinity
      const bTime = b.start
        ? Math.abs(new Date(b.start).getTime() - currentTime.getTime())
        : Infinity
      return aTime - bTime
    })

  const closestAppointment = closestAppointments?.[0]
  const isLoadingClosestAppointment = isLoadingAllAppointments

  const { data: closestEncounter } = useSearchOne(AppointmentEncounter, {
    appointment: closestAppointment?.reference,
  })

  // TODO: Support creating a new appointment from the combobox

  if (isLoadingClosestAppointment) {
    return (
      <ItemContent className="h-10">
        <Loader />
      </ItemContent>
    )
  }

  return (
    <>
      <ItemContent>
        <ItemTitle>
          <AppointmentCombobox
            appointments={closestAppointments ?? []}
            onSelect={(value) => linkAppointment(value)}
          >
            <Button
              variant="outline"
              className="flex items-center gap-2"
              loading={isPending}
            >
              <h2 className="flex items-center gap-1">
                {closestAppointment && (
                  <>
                    {closestAppointment.getDisplay()}
                    <DotSeparator />
                    <span className="text-foreground-secondary">
                      <DateTimeDisplay value={closestAppointment.start} />
                    </span>
                  </>
                )}
                {!closestAppointment && 'Link event…'}
              </h2>
              {closestAppointment &&
                closestAppointment.practitioners.length > 0 && (
                  <Avatar
                    profile={closestAppointment.practitioners[0]}
                    size="icon"
                  />
                )}
            </Button>
          </AppointmentCombobox>
        </ItemTitle>
      </ItemContent>
      {closestAppointment && (
        <ItemActions>
          <Button
            className="flex gap-2"
            loading={isPending}
            onClick={() =>
              linkAppointment(closestAppointment, closestEncounter ?? undefined)
            }
          >
            <IconLinkPlus className="size-4 shrink-0" />
            Link event
          </Button>
        </ItemActions>
      )}
    </>
  )
}
